import React, { useEffect, useState } from 'react';
import { InventoryPanel } from './';
import type { InventoryItem } from './ItemCard';
import type { Item } from '../../types/game';
import apiService from '../../services/api';

export interface InventoryMenuProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string | undefined;
  playerId?: string | undefined;
}

const InventoryMenu: React.FC<InventoryMenuProps> = ({
  isOpen,
  onClose,
  sessionId,
  playerId
}) => {
  const [fullItems, setFullItems] = useState<Item[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);

  // Fetch full item data when menu opens
  useEffect(() => {
    const fetchItemData = async () => {
      if (!isOpen || !sessionId || !playerId) {
        setFullItems([]);
        return;
      }

      setLoadingItems(true);
      setItemsError(null);

      try {
        const items = await apiService.getPlayerItems(sessionId, playerId);
        setFullItems(items);
      } catch (error) {
        console.error('Failed to fetch item data:', error);
        setItemsError(error instanceof Error ? error.message : 'Failed to load item details');
        // Fallback to empty array - component will still show basic info
        setFullItems([]);
      } finally {
        setLoadingItems(false);
      }
    };

    fetchItemData();
  }, [isOpen, sessionId, playerId]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Convert fetched items to InventoryItem format for InventoryPanel
  let normalizedInventory: InventoryItem[] = [];
  
  try {
    normalizedInventory = loadingItems 
      ? [] // Show loading state
      : itemsError 
        ? [] // Show error state
        : fullItems.map((item) => ({
            id: item.tag,
            name: item.name,
            description: item.description,
            quantity: 1,
            type: 'item' as const,
            // Store the full Item object for rich text rendering
            fullItem: item
          }));
  } catch (error) {
    console.error('Error normalizing inventory:', error);
    setItemsError('Error processing item data');
    normalizedInventory = [];
  }

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      zIndex: 9999,
      overflow: 'hidden'
    }}>
      {/* Backdrop */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <div 
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: '28rem',
          height: '80vh',
          maxHeight: '80vh',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '0.75rem',
          transition: 'transform 0.3s ease-in-out'
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div 
            className="px-6 py-6 flex items-center border-b border-white border-opacity-20 flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.6), rgba(16, 185, 129, 0.6))',
              backdropFilter: 'blur(12px) saturate(150%)',
              borderTopLeftRadius: '0.75rem',
              borderTopRightRadius: '0.75rem'
            }}
          >
            <h2 className="text-xl font-bold text-white flex-1 text-center">🎒 Inventory</h2>
            <button
              onClick={onClose}
              className="transition-all duration-200 p-2 rounded-full hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              aria-label="Close inventory"
              style={{
                minWidth: '2rem',
                minHeight: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '0.5rem',
                color: '#86efac', // green-300
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#86efac'; // green-300
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-8 py-6 flex-1 overflow-y-auto">
            {loadingItems ? (
              <div className="text-center py-8">
                <div className="text-white text-lg">Loading items...</div>
              </div>
            ) : itemsError ? (
              <div className="text-center py-8">
                <div className="text-red-300 text-lg mb-2">Error loading items</div>
                <div className="text-white text-sm">{itemsError}</div>
              </div>
            ) : (
              <InventoryPanel 
                items={normalizedInventory}
                className="bg-transparent border-white border-opacity-20 text-white"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryMenu;
